import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Smoothly scroll to the booking calendar section.
export function scrollToCalendar() {
  const el = document.getElementById("booking");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

// Reusable CTA button. All CTAs route to the booking calendar.
export function CTAButton({ children, variant = "gold", className = "", testId, full = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={scrollToCalendar}
      className={`cta-btn cta-${variant} ${full ? "w-full" : ""} text-base md:text-lg px-7 md:px-9 py-4 ${className}`}
    >
      {children}
    </button>
  );
}

// Small uppercase section label (gold).
export function SectionLabel({ children, color = "#F5A623", className = "" }) {
  return (
    <span
      className={`font-head inline-block text-xs md:text-sm font-extrabold uppercase mb-3 ${className}`}
      style={{ color, letterSpacing: "0.18em" }}
    >
      {children}
    </span>
  );
}

// Fade/slide-in on scroll wrapper.
export function Reveal({ children, delay = 0, y = 26, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Animated count-up that triggers once when scrolled into view.
export function CountUp({ end = 0, prefix = "", suffix = "", duration = 1600, decimals = 0, className = "" }) {
  const reduce = useReducedMotion();
  const [val, setVal] = useState(reduce ? end : 0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (reduce) {
      setVal(end);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const step = (now) => {
              const p = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(end * eased);
              if (p < 1) requestAnimationFrame(step);
              else setVal(end);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, reduce]);

  const display =
    decimals > 0 ? Number(val).toFixed(decimals) : Math.round(val).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

// Decorative geometric sun-ray pattern (subtle, not clip art).
export function SunRays({ color = "#F5A623", opacity = 0.14, originX = "100%", originY = "0%" }) {
  const rays = Array.from({ length: 22 });
  return (
    <svg
      className="sun-rays"
      width="100%"
      height="100%"
      preserveAspectRatio="xMaxYMin slice"
      aria-hidden="true"
      style={{ opacity }}
    >
      <g>
        {rays.map((_, i) => {
          const angle = (i / rays.length) * 180 + 90; // sweep across
          const rad = (angle * Math.PI) / 180;
          const x2 = `calc(${originX} + ${Math.cos(rad) * 2200}px)`;
          const y2 = `calc(${originY} + ${Math.sin(rad) * 2200}px)`;
          return (
            <line
              key={`ray-${i}`}
              x1={originX}
              y1={originY}
              x2={x2}
              y2={y2}
              stroke={color}
              strokeWidth={i % 2 === 0 ? 1.5 : 0.6}
            />
          );
        })}
      </g>
    </svg>
  );
}

// Elegant image placeholder (gradient + sun motif) for swappable photos.
export function ImagePlaceholder({ label = "Photo coming soon", className = "", ratio = "aspect-[4/3]" }) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${ratio} ${className}`}
      style={{
        background: "linear-gradient(135deg, #1A2B4A 0%, #2c4a7a 55%, #F5A623 160%)",
      }}
    >
      <div className="absolute inset-0" style={{ opacity: 0.18 }}>
        <SunRays color="#ffffff" opacity={1} originX="15%" originY="15%" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <div
          className="mb-2 flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: "rgba(245,166,35,0.9)" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A2B4A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
          </svg>
        </div>
        <span className="font-head text-sm font-semibold text-white/85">{label}</span>
      </div>
    </div>
  );
}
